import { FC, HTMLInputTypeAttribute, ReactNode } from "react"
import { Control, Controller, FieldError, FieldValues, UseFormRegister } from "react-hook-form"
import WarningSvg from '@/shared/assets/images/Warning.svg'
import ReactSelect, { GroupBase, InputActionMeta, OptionsOrGroups } from "react-select"
import ArrowDown from '@/shared/assets/images/ArrowDown.svg'
import DatePicker from "react-datepicker"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import { SelectComponents } from "react-select/dist/declarations/src/components"
import { createPortal } from "react-dom"

interface DatepickerForm {
    control: Control<FieldValues>
    label: string,
    id: string,
    type: "datepicker"
    required: boolean
    placeholder?: string
    error?: FieldError
}

interface SelectForm {
    control: Control<FieldValues>
    label: string,
    id: string,
    isMulti?: boolean
    isLoading?: boolean
    type: "select"
    required: boolean
    placeholder?: string
    noOptionsMessage?: (obj: {
        inputValue: string;
    }) => React.ReactNode
    error?: FieldError
    options: OptionsOrGroups<any, GroupBase<any>>
    components?: Partial<SelectComponents<any, boolean, GroupBase<any>>>
    onInputChange?: (inputValue: string, actionMeta?: InputActionMeta) => void
}

interface TextareaForm {
    register: UseFormRegister<FieldValues>
    label: string,
    id: string,
    type: 'textarea'
    required: boolean
    placeholder?: string
    error?: FieldError
    rows?: number
    cols?: number
    maxLength?: number
    minLength?: number
}

interface DefaultForm {
    register: UseFormRegister<FieldValues>
    label: string,
    id: string,
    type?: HTMLInputTypeAttribute
    required: boolean
    placeholder?: string
    error?: FieldError
    min?: number
    max?: number
    maxLength?: number
    minLength?: number
}

interface ToggleSwitchForm {
    register: UseFormRegister<FieldValues>
    label: string,
    id: string,
    type: 'toggle'
    error?: FieldError
}

type FormInputProps = DefaultForm | DatepickerForm | SelectForm | TextareaForm | ToggleSwitchForm

const ToggleSwitchView: FC<ToggleSwitchForm> = ({ register, label, id, error }) => {
    return (
        <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <input
                className="relative w-[3.25rem] h-7 bg-[#B9C9C6] checked:bg-none checked:bg-color-primary border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 ring-0 ring-transparent focus:ring-transparent ring-offset-white focus:outline-0 appearance-none dark:bg-black/20 dark:checked:bg-primary dark:focus:ring-offset-white/10 before:inline-block before:w-6 before:h-6 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:shadow before:rounded-full before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 "
                type="checkbox"
                {...register(id)}
            />
        </div>
    )
}

const TextareaView: FC<TextareaForm> = ({ register, minLength, maxLength, placeholder, error, rows, cols, required, id, label }) => {
    return (
        <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <textarea 
                placeholder={placeholder}
                minLength={minLength}
                maxLength={maxLength}
                rows={rows}
                cols={cols}
                className={`w-full outline-0 resize-none text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                {...register(id, { required })} 
            />
        </div>
    )
}

const DefaultView: FC<DefaultForm> = ({ register, min, max, minLength, maxLength, placeholder, error, type = "text", required, id, label }) => {
    return (
        <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <input 
                placeholder={placeholder}
                type={type}
                min={min}
                max={max}
                minLength={minLength}
                maxLength={maxLength}
                className={`w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                {...register(id, { required })} 
            />
        </div>
    )
}

const SelectView: FC<SelectForm> = ({ placeholder, isLoading, isMulti, error, control, options, id, required, label, components, noOptionsMessage, onInputChange }) => {
    return (
        <div className={`flex relative flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <Controller
                control={control}
                name={id}
                render={({ field }) => (
                <ReactSelect 
                    {...field}
                    classNames={{
                        control: () => 'w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none ',
                        valueContainer: () => '!border-0 !outline-0 !p-0 text-color-dark text-lg',
                        placeholder: () => 'text-color-dark/70 text-base !m-0 text-color-dark',
                        menu: () => '!left-[0]',
                        input: () => '!m-0 !p-0',
                        menuPortal: () => '!z-[9999]'
                    }}
                    isMulti={isMulti}
                    name={field.name}
                    classNamePrefix="react_select_custom"
                    ref={field.ref}
                    noOptionsMessage={
                        noOptionsMessage
                        ? noOptionsMessage
                        : () => 'Нет опций'
                    }
                    isLoading={isLoading}
                    value={isMulti ? options.filter(option => field.value?.find((i: any) => i === option.value)) : (options.find(option => option.value === field.value) || "")}
                    onChange={(e) => isMulti ? field.onChange(e.map((i: any) => i.value)) : field.onChange(e.value)}
                    options={options}
                    placeholder={placeholder}
                    required={required}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    onInputChange={onInputChange}
                    components={{
                        DropdownIndicator: () => <ArrowDown height={20} width={20}/>,
                        IndicatorSeparator: () => null,
                        ...components
                    }}
                />)}
            />
        </div>
    )
}

const CalendarContainer = ({ children }: { children: ReactNode}) => {
    return createPortal(children, document.body);
  };

const DatepickerView: FC<DatepickerForm> = ({ placeholder, error, control, id, required, label }) => {
    return (
        <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error && 'border-color-danger'}`}>
            <div className="flex justify-between w-full items-center">
                <label className="font-semibold uppercase text-base text-[#828282]">
                    {label}
                </label>
                {error &&
                    <label className="text-base flex items-center text-color-danger">
                        <WarningSvg width={20} height={20} className="me-1" />
                        {error.message}
                    </label>
                }
            </div>
            <Controller
                control={control}
                name={id}
                render={({ field }) => (
                    <DatePicker 
                        placeholderText={placeholder}
                        required={required}
                        selected={field.value}
                        wrapperClassName="react-datepicker-form"
                        dateFormat={"dd.MM.yyyy"}
                        showIcon
                        onChange={(date: Date) => field.onChange(date)}
                        enableTabLoop={false}
                        popperProps={{
                            placement: 'bottom',
                        }}
                        popperClassName="z-[9999]"
                        popperContainer={CalendarContainer}
                        icon={(<CalendarIcon width={24} height={24}/>)}
                    />
                )}
            />
        </div>
    )
}

export const FormInput: FC<FormInputProps> = (props) => {
    switch(props.type) {
    case 'datepicker':
        return <DatepickerView {...props as DatepickerForm} />
    case 'select':
        return <SelectView {...props as SelectForm} />
    case 'textarea':
        return <TextareaView {...props as TextareaForm} />
    case 'toggle':
        return <ToggleSwitchView {...props as ToggleSwitchForm}/>
    default:
        return <DefaultView {...props as DefaultForm} />
    }
}